import React, { useEffect, useRef } from "react";

/**
 * GalaxyBackground (rev 3) — system-map nodes orbit a central 3D orb as
 * NAKED LOGOS (no boxes). Z-depth is real: icons behind the orb get
 * occluded + blurred, icons in front render sharp. Single canvas, painter's
 * algorithm depth sort, 3D perspective projection of orbit positions.
 *
 * Click any icon → fires its voice command via 'rawco-icon-click'.
 */

const PALETTE = {
  brain: "#FFD24A", orch: "#A78BFA", agent: "#7CFFB7", channel: "#FF7FB1",
  integ: "#FFC555", data: "#9AE6FF",
  social_ig: "#E1306C", social_fb: "#1877F2", social_tt: "#69C9D0", social_yt: "#FF0000",
  capital: "#D4AF37", stripe: "#635BFF", spotify: "#1DB954", ring: "#1E78E0",
  bbc: "#BB1919", gmail: "#EA4335",
};

// 3D coordinates: each node lives on a sphere around the orb (no flat ring).
// Each glyph is the official monogram so it's instantly recognisable. We
// stop drawing background squares entirely — the symbol IS the icon.
const NODES = [
  // ORCH + agents — closest layer
  { id: "orch",     g: "ORCH", group: "orch",    r: 1.0, cmd: "show me the system map" },
  { id: "tom",      g: "TOM",  group: "agent",   r: 1.0, cmd: "open agent studio and run Tom" },
  { id: "sarah",    g: "SARAH",group: "agent",   r: 1.0, cmd: "open agent studio and run Sarah" },
  { id: "eva",      g: "EVA",  group: "agent",   r: 1.0, cmd: "open agent studio and run Eva" },
  { id: "scout",    g: "SCOUT",group: "agent",   r: 1.0, cmd: "open agent studio and run Scout" },
  { id: "dobby",    g: "DOBBY",group: "agent",   r: 1.0, cmd: "open agent studio and run Dobby" },
  { id: "claude",   g: "C",    group: "brain",   r: 0.95, cmd: "tell me about your AI brain" },
  { id: "gemini",   g: "G",    group: "brain",   r: 0.95, cmd: "tell me about your AI brain" },
  // Channels
  { id: "voice",    g: "🎙",   group: "channel", r: 1.4, cmd: "list my voice options" },
  { id: "slack",    g: "#",    group: "channel", r: 1.4, cmd: "open slack" },
  { id: "imessage", g: "💬",   group: "channel", r: 1.4, cmd: "show me my conversations" },
  { id: "bbc",      g: "BBC",  group: "bbc",     r: 1.4, cmd: "put the live BBC news on" },
  // Integrations + socials outer
  { id: "capital",  g: "$",    group: "capital", r: 1.8, cmd: "show me my live capital P&L" },
  { id: "stripe",   g: "S",    group: "stripe",  r: 1.8, cmd: "show me my stripe balance" },
  { id: "tavily",   g: "T",    group: "integ",   r: 1.8, cmd: "search the web for tech news" },
  { id: "eleven",   g: "11",   group: "integ",   r: 1.8, cmd: "list my voice options" },
  { id: "spotify",  g: "♫",    group: "spotify", r: 1.8, cmd: "open spotify" },
  { id: "ig",       g: "IG",   group: "social_ig", r: 1.8, cmd: "open the social composer for instagram" },
  { id: "fb",       g: "f",    group: "social_fb", r: 1.8, cmd: "open the social composer for facebook" },
  { id: "tt",       g: "♪",    group: "social_tt", r: 1.8, cmd: "open the social composer for tiktok" },
  { id: "yt",       g: "▶",    group: "social_yt", r: 1.8, cmd: "show me my recent youtube videos" },
  { id: "gmail",    g: "M",    group: "gmail",   r: 1.8, cmd: "open gmail" },
  { id: "calendar", g: "📅",   group: "data",    r: 1.8, cmd: "open google calendar" },
  { id: "drive",    g: "△",    group: "data",    r: 1.8, cmd: "open google drive" },
  { id: "github",   g: "</>",  group: "data",    r: 1.8, cmd: "open github" },
  { id: "ring",     g: "◉",    group: "ring",    r: 1.8, cmd: "show me the doorbell" },
];

export default function GalaxyBackground({ active }) {
  const canvasRef = useRef(null);
  const partsRef = useRef([]);
  const starsRef = useRef(null);
  const rafRef = useRef(null);
  const sizeRef = useRef({ w: 0, h: 0 });
  const mouseRef = useRef({ x: -1, y: -1, over: -1 });

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const w = window.innerWidth, h = window.innerHeight;
      canvas.width = w * dpr; canvas.height = h * dpr;
      canvas.style.width = w + "px"; canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      sizeRef.current = { w, h };
    };
    resize();
    window.addEventListener("resize", resize);

    // Star field
    const buildStars = (count, depth) => Array.from({ length: count }).map(() => ({
      x: Math.random(), y: Math.random(),
      r: 0.3 + Math.random() * (depth === 2 ? 1.6 : depth === 1 ? 0.9 : 0.6),
      tw: Math.random() * Math.PI * 2, twSpeed: 0.5 + Math.random() * 1.5,
    }));
    starsRef.current = [
      { stars: buildStars(160, 0), driftX: 0.0001, driftY: 0.00004 },
      { stars: buildStars(90,  1), driftX: 0.0003, driftY: 0.00012 },
      { stars: buildStars(45,  2), driftX: 0.0007, driftY: 0.00026 },
    ];

    // Distribute nodes evenly on each radial shell using a Fibonacci sphere
    // mapping per-shell so they don't cluster on the equator.
    const byShell = {};
    NODES.forEach((n, i) => { (byShell[n.r] = byShell[n.r] || []).push({ n, i }); });
    partsRef.current = [];
    for (const r of Object.keys(byShell)) {
      const shell = byShell[r];
      shell.forEach(({ n, i }, k) => {
        const t = k / shell.length;
        const phi = Math.acos(1 - 2 * t);                 // 0..π latitude
        const theta = Math.PI * (1 + Math.sqrt(5)) * k;   // golden-angle longitude
        partsRef.current.push({
          ...n,
          color: PALETTE[n.group] || "#9AE6FF",
          shell: parseFloat(r),
          phi, theta,
          phiSpeed:  0.00012 * (0.5 + Math.random()),
          thetaSpeed: 0.00020 * (0.6 + Math.random()) * (Math.random() < 0.5 ? 1 : -1),
          size: n.id === "orch" ? 18 : 14,
          x: 0, y: 0, z: 0,
        });
      });
    }

    let lastT = 0;
    let paused = false;
    const render = (ts) => {
      if (paused) { rafRef.current = requestAnimationFrame(render); return; }
      // Cap delta to 33ms so a paused tab / GC pause doesn't fast-forward
      // the entire animation by 500ms+ when resumed (which is what makes
      // it look "jolting"). Fixed visual cadence regardless of frame rate.
      const rawDt = ts - lastT;
      const dt = Math.min(rawDt || 16, 33);
      lastT = ts;
      const { w, h } = sizeRef.current;
      const cx = w * 0.5, cy = h * 0.5;
      const minDim = Math.min(w, h);
      const orbR = minDim * 0.08;    // 3D orb radius in screen px
      const shellPx = minDim * 0.22;  // base shell radius scale
      const perspectiveZ = minDim;     // simulated camera depth

      // Nebula base
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(w, h) * 0.7);
      g.addColorStop(0,    "rgba(38, 0, 70, 1)");
      g.addColorStop(0.18, "rgba(18, 8, 56, 1)");
      g.addColorStop(0.45, "rgba(6, 4, 26, 1)");
      g.addColorStop(1,    "rgba(0, 0, 4, 1)");
      ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);

      // Swirling clouds
      ctx.globalCompositeOperation = "screen";
      const swirl = ts * 0.000018;
      for (let k = 0; k < 4; k++) {
        const a = swirl + k * Math.PI * 0.5;
        const bx = cx + Math.cos(a) * w * 0.22;
        const by = cy + Math.sin(a * 1.3) * h * 0.22;
        const cg = ctx.createRadialGradient(bx, by, 0, bx, by, minDim * 0.32);
        const hues = ["rgba(120, 40, 200, 0.16)", "rgba(20, 120, 200, 0.15)", "rgba(220, 80, 180, 0.12)", "rgba(40, 200, 220, 0.12)"];
        cg.addColorStop(0, hues[k]); cg.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = cg; ctx.fillRect(0, 0, w, h);
      }
      ctx.globalCompositeOperation = "source-over";

      // Stars
      for (const layer of starsRef.current) {
        for (const s of layer.stars) {
          s.x += layer.driftX; if (s.x > 1) s.x -= 1;
          s.y += layer.driftY; if (s.y > 1) s.y -= 1;
          const tw = 0.6 + 0.4 * Math.sin(ts * 0.001 * s.twSpeed + s.tw);
          ctx.fillStyle = `rgba(230, 230, 255, ${tw})`;
          ctx.beginPath(); ctx.arc(s.x * w, s.y * h, s.r, 0, Math.PI * 2); ctx.fill();
        }
      }

      // Update + project icon 3D positions
      const parts = partsRef.current;
      for (const p of parts) {
        p.phi += p.phiSpeed * dt;
        p.theta += p.thetaSpeed * dt;
        const R = p.shell * shellPx;
        // Spherical → Cartesian
        const sx = R * Math.sin(p.phi) * Math.cos(p.theta);
        const sy = R * Math.cos(p.phi);
        const sz = R * Math.sin(p.phi) * Math.sin(p.theta);
        p.z = sz;
        // Perspective projection
        const scale = perspectiveZ / (perspectiveZ + sz);
        p.x = cx + sx * scale;
        p.y = cy + sy * scale * 0.85;   // slight squish for parallax feel
        p.scale = scale;
      }

      // Painter sort: far → near
      const sorted = [...parts].sort((a, b) => b.z - a.z);

      // Draw far half (z > 0, behind orb) — these will be occluded by orb glow
      for (let i = 0; i < sorted.length; i++) {
        const p = sorted[i];
        if (p.z <= 0) continue;
        drawIcon(ctx, p, ts, /*behindOrb=*/true);
      }

      // BRAIN ORB — sphere with depth gradient, soft halo, pulsing
      const pulse = 0.65 + 0.35 * Math.sin(ts * 0.0022);
      const halo = ctx.createRadialGradient(cx, cy, orbR * 0.5, cx, cy, orbR * 3.5);
      halo.addColorStop(0,   `rgba(255, 210, 74, ${0.6 * pulse})`);
      halo.addColorStop(0.4, `rgba(255, 184, 0, ${0.18 * pulse})`);
      halo.addColorStop(1,   "rgba(0, 0, 0, 0)");
      ctx.fillStyle = halo;
      ctx.beginPath(); ctx.arc(cx, cy, orbR * 3.5, 0, Math.PI * 2); ctx.fill();
      // Orb body
      const orb = ctx.createRadialGradient(cx - orbR * 0.35, cy - orbR * 0.4, orbR * 0.2, cx, cy, orbR);
      orb.addColorStop(0,   "rgba(255, 240, 170, 1)");
      orb.addColorStop(0.4, "rgba(255, 200, 60, 0.95)");
      orb.addColorStop(0.85,"rgba(120, 70, 0, 0.95)");
      orb.addColorStop(1,   "rgba(40, 20, 0, 0.95)");
      ctx.fillStyle = orb;
      ctx.beginPath(); ctx.arc(cx, cy, orbR, 0, Math.PI * 2); ctx.fill();
      // Specular highlight
      ctx.fillStyle = "rgba(255, 255, 255, 0.4)";
      ctx.beginPath(); ctx.arc(cx - orbR * 0.35, cy - orbR * 0.45, orbR * 0.16, 0, Math.PI * 2); ctx.fill();
      // Rim light
      ctx.strokeStyle = `rgba(255, 210, 74, ${0.7 * pulse})`;
      ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.arc(cx, cy, orbR, 0, Math.PI * 2); ctx.stroke();

      // Draw near half (z <= 0, in front of orb)
      for (let i = 0; i < sorted.length; i++) {
        const p = sorted[i];
        if (p.z > 0) continue;
        drawIcon(ctx, p, ts, /*behindOrb=*/false);
      }

      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = e.clientX - rect.left;
      mouseRef.current.y = e.clientY - rect.top;
      mouseRef.current.over = -1;
      const parts = partsRef.current;
      for (let i = 0; i < parts.length; i++) {
        const p = parts[i];
        if (p.z > 0) continue; // only hit front-half icons
        const dx = mouseRef.current.x - p.x, dy = mouseRef.current.y - p.y;
        if (Math.sqrt(dx * dx + dy * dy) < p.size * p.scale * 1.5) {
          mouseRef.current.over = i; break;
        }
      }
      canvas.style.cursor = mouseRef.current.over >= 0 ? "pointer" : "default";
    };
    const onClick = () => {
      if (mouseRef.current.over < 0) return;
      const p = partsRef.current[mouseRef.current.over];
      if (!p?.cmd) return;
      window.dispatchEvent(new CustomEvent("rawco-icon-click", { detail: { cmd: p.cmd, label: p.id } }));
    };
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("click", onClick);

    // Pause the rAF loop when the tab is hidden — browsers throttle background
    // rAF to ~1Hz which causes the "canvas freezes after 30 seconds" complaint
    // when the user comes back. Resetting lastT on resume avoids a big dt jump.
    const onVis = () => {
      if (document.hidden) {
        paused = true;
      } else {
        paused = false;
        lastT = 0; // first frame after resume uses dt=16ms (clamped)
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("click", onClick);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [active]);

  if (!active) return null;
  return (
    <canvas
      ref={canvasRef}
      data-testid="galaxy-background"
      style={{
        position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
        zIndex: 0, pointerEvents: "auto",
      }}
    />
  );
}

/**
 * drawIcon — paints a naked glyph (no box) at p.x/p.y, scaled by p.scale.
 * When `behindOrb` is true the icon gets blurred + dimmed, simulating
 * the orb's glow obscuring it.
 */
function drawIcon(ctx, p, ts, behindOrb) {
  const fontPx = Math.max(8, p.size * p.scale * 1.6);
  ctx.save();
  if (behindOrb) {
    // Behind-orb icons just go faded (no CSS blur — too expensive,
    // tanks frame rate causing the "jolting" the owner reported).
    ctx.globalAlpha = 0.35;
    ctx.shadowColor = p.color;
    ctx.shadowBlur = 8;
  } else {
    ctx.shadowColor = p.color;
    ctx.shadowBlur = 12 * p.scale;
  }
  ctx.font = `900 ${fontPx}px 'Orbitron', system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // Black shadow for contrast against the nebula
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  ctx.fillText(p.g, p.x + 1, p.y + 1);
  ctx.fillStyle = p.color;
  ctx.fillText(p.g, p.x, p.y);
  ctx.restore();
}
